import { toast } from 'react-toastify';
import statusModal from '@pages/Forms/components/modals/statusModal';
import SubmissionModal from '@pages/Forms/components/modals/SubmissionModal';
import { useApp, useModal, useRequest } from '@stores';
import { LoadingMessages, RequestStatus, RequestType, ResponseStatuses, StorageScopes } from '@src/utils/enums';
import { queryClient } from '@src/api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '@src/api/services/dashboard';
import { router } from '@src/Router';

const handleSuccess = (signed, addVC, body, store, dispatch, submissionModal) => {
  const { setIsSavingProgress, setIsLoading } = useApp.getState();
  const { openModal } = useModal.getState();

  setIsSavingProgress(false);
  setIsLoading(false);
  if (signed) {
    toast.success('Violation Complaint has been successfully submitted!');
    openModal(submissionModal, {
      msg: body?.msg,
      status: body?.status ?? ResponseStatuses.Success,
    });
    router.navigate({ to: '/dashboard' });
  } else {
    !addVC && toast.success('Violation Complaint has been successfully saved!');
    dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: body.data });
    dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: body?.data });
    dispatch({ type: 'UPDATE_REQUEST_OBJECT', payload: { requestStatus: RequestStatus.Draft } });
  }
};

const violationsFormDataBundle = {
  name: 'violationsFormData',

  getReducer: () => {
    const initialState = {
      violationComplaints: [],
      errors: {},
    };

    return (state = initialState, { type, payload }) => {
      switch (type) {
        case 'RESET_VIOLATION_COMPLAINT':
          return initialState;
        case 'UPDATE_VIOLATION_COMPLAINT':
          return {
            ...state,
            violationComplaints: [{ ...state.violationComplaints[0], ...payload }],
          };
        case 'UPDATE_VIOLATION_COMPLAINT_CONTACTS':
          return {
            ...state,
            violationComplaints: [
              {
                ...state.violationComplaints[0],
                contacts: payload,
              },
            ],
          };
        case 'UPDATE_VIOLATION_COMPLAINT_IS_ALLEGED_VIOLATOR':
          return {
            ...state,
            violationComplaints: [
              {
                ...state.violationComplaints[0],
                isAllegedViolatorKnown: payload,
              },
            ],
          };
        case 'UPDATE_IS_ANONYMOUS':
          return {
            ...state,
            violationComplaints: [
              {
                ...state.violationComplaints[0],
                isAnonymous: payload,
              },
            ],
          };
        case 'UPDATE_VIOLATION_COMPLAINT_ERRORS':
          return { ...state, errors: payload };
        default:
          return state;
      }
    };
  },

  selectIsAnonymous: (state) =>
    state.violationsFormData.violationComplaints.length > 0 &&
    state.violationsFormData.violationComplaints[0].isAnonymous,
  selectViolationComplaintData: (state) => state.violationsFormData.violationComplaints[0],
  selectViolationComplaintID: (state) => state.violationsFormData.violationComplaints[0].violationComplaintID,
  selectViolationComplaintErrors: (state) => state.violationsFormData.errors,

  doFetchViolationComplaint:
    (projectID, requestID, requestStatus, isGenerate = false, archived) =>
    ({ apiGet, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();
      const { setSelectedRequestId } = useRequest.getState();

      !isGenerate && setIsLoading(true, 'Retrieving Violation Complaint Information...');
      store.doResetViolationComplaintRequest();
      store.doResetRequestData();

      const uri =
        '/api/ViolationComplaint/getViolationComplaint?' +
        new URLSearchParams({
          projectID,
          requestID,
        });

      return apiGet(uri, (err, body) => {
        !isGenerate && setIsLoading(false);
        if (!err && body.status === ResponseStatuses.Success) {
          const responseData = { ...body?.data, archived };

          dispatch({ type: 'UPDATE_REQUEST_API_DATA', payload: responseData });
          dispatch({ type: 'UPDATE_REQUEST_FORM_DATA', payload: responseData });
          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: { requestStatus: requestStatus },
          });
          dispatch({ type: 'UPDATE_VIOLATION_COMPLAINT', payload: responseData?.request?.violationComplaints?.[0] });

          if (isGenerate) {
            store.doGenerateDraftDocuments();
          } else {
            setSelectedRequestId('6');
            router.navigate({
              to: '/forms/$id',
              params: { id: String(requestID) },
            });
            store
              .doFetchUserFilesDetails({
                componentID: responseData?.request?.violationComplaints?.[0]?.violationComplaintID,
                scope: StorageScopes.ProjectFolder,
              })
              .finally(() => {
                dispatch({ type: 'SET_LOADING_STATE', payload: false });
              });
          }
        } else {
          dispatch({ type: 'VIOLATION_COMPLAINT_UPDATE_ERROR', payload: err });
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while retrieving the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Retrieval Error',
          });
          dispatch({ type: 'SET_LOADING_STATE', payload: false });
        }
      });
    },
  doAddViolationComplaint:
    () =>
    ({ apiPost, dispatch, store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal, closeModal } = useModal.getState();

      setIsLoading(true, 'Creating Violation Complaint...');

      const uri = '/api/ViolationComplaint/addViolationComplaint';

      return apiPost(uri, null, (err, body) => {
        setIsLoading(false);
        if (!err && body.status === ResponseStatuses.Success) {
          dispatch({
            type: 'UPDATE_REQUEST_API_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID },
            },
          });
          dispatch({
            type: 'UPDATE_REQUEST_FORM_DATA',
            payload: {
              projectID: body.data.projectID,
              projectVersion: body.data.projectVersion,
              request: { requestID: body.data.requestID },
            },
          });
          dispatch({
            type: 'UPDATE_REQUEST_OBJECT',
            payload: {
              requestStatus: RequestStatus.Draft,
              requestID: body.data.requestID,
              requestType: RequestType.ViolationComplaint,
            },
          });
          dispatch({
            type: 'UPDATE_VIOLATION_COMPLAINT',
            payload: { violationComplaintID: body.data.violationComplaintID },
          });
          store.doUpdateViolationComplaint(store.selectRequestFormData(), true);
          router.navigate({ to: '/forms' });
          closeModal();
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
        } else {
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while creating the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Creation Error',
          });
        }
      });
    },
  doAddAnonymousViolationComplaint:
    () =>
    ({ apiPost, dispatch }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal, closeModal } = useModal.getState();

      setIsLoading(true, 'Creating Anonymous Violation Complaint...');

      const uri = '/api/ViolationComplaint/addAnonymousViolationComplaint';
      const skipAuth = true;

      return apiPost(
        uri,
        null,
        (err, body) => {
          setIsLoading(false);
          if (!err && body.status === ResponseStatuses.Success) {
            dispatch({
              type: 'UPDATE_REQUEST_API_DATA',
              payload: {
                projectID: body.data.projectID,
                projectVersion: body.data.projectVersion,
                request: { requestID: body.data.requestID },
              },
            });
            dispatch({
              type: 'UPDATE_REQUEST_FORM_DATA',
              payload: {
                projectID: body.data.projectID,
                projectVersion: body.data.projectVersion,
                request: { requestID: body.data.requestID },
              },
            });
            dispatch({
              type: 'UPDATE_REQUEST_OBJECT',
              payload: { requestStatus: RequestStatus.Draft, requestType: RequestType.ViolationComplaint },
            });
            dispatch({
              type: 'UPDATE_VIOLATION_COMPLAINT',
              payload: { violationComplaintID: body.data.violationComplaintID },
            });
            router.navigate({ to: '/forms' });
            closeModal();
          } else {
            openSecondaryModal(statusModal, {
              msg: body?.msg ?? err?.msg ?? 'An error occurred while creating the request, please try again',
              status: body?.status ?? err?.status ?? 'Request Creation Error',
            });
          }
        },
        skipAuth
      );
    },
  doUploadAndUpdateViolationComplaint:
    (data, status) =>
    ({ store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      const signed = Boolean(data.request.isSubmit);

      setIsSavingProgress(true);
      setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);

      if (store.selectSelectedFiles()?.length > 0) {
        // Format to FormData
        const formdata = new FormData();
        formdata.append('overwrite', false);
        formdata.append('isAnonymous', false);
        store.selectSelectedFiles().forEach((item) => {
          const fileMetaData = {
            ProjectID: store.selectProjectID(),
            RequestID: store.selectRequestID(),
            Version: 0,
            Type: status,
            Tags: item?.tags,
          };
          formdata.append('files', item?.file);
          formdata.append('fileMetaData', JSON.stringify(fileMetaData));
        });

        // Upload Files and Save Request
        Promise.all([store.doUploadFiles(formdata)])
          .then((results) => {
            if (results?.[0]?.status === ResponseStatuses.Success) {
              store.doUpdateViolationComplaint(data);
              store.doResetUsersFileData();
              store.doFetchUserFilesDetails({
                componentID: store.selectViolationComplaintID(),
                scope: StorageScopes.ProjectFolder,
              });
            } else {
              // if any of the files failed to upload
              setIsSavingProgress(false);
              signed && setIsLoading(false);
              store.doResetUsersFileData();
              store.doFetchUserFilesDetails({
                componentID: store.selectViolationComplaintID(),
                scope: StorageScopes.ProjectFolder,
              });
              openSecondaryModal(statusModal, {
                status: 'ERROR! Failed Uploads',
                msg: (
                  <>
                    <p>{results?.[0]?.msg}</p>
                    <p>Retry saving/submitting your Violation Complaint in a few minutes.</p>
                  </>
                ),
                saveText: 'Return to request',
              });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        // If no files were selected, just update VC
        store.doUpdateViolationComplaint(data);
      }
    },
  doUpdateViolationComplaint:
    (data, addVC) =>
    ({ apiPut, dispatch, store }) => {
      const { setIsLoading, setIsSavingProgress } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      const signed = Boolean(data.request.isSubmit);
      setIsSavingProgress(true);
      setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);

      const uri = '/api/ViolationComplaint/updateViolationComplaint';

      return apiPut(uri, data, (err, body) => {
        if (
          !err &&
          [ResponseStatuses.Success, ResponseStatuses.SuccessWithWarning, ResponseStatuses.Warning].includes(
            body?.status
          )
        ) {
          handleSuccess(signed, addVC, body, store, dispatch, SubmissionModal);
          queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
        } else {
          setIsSavingProgress(false);
          setIsLoading(false);
          openSecondaryModal(statusModal, {
            msg: body?.msg ?? err?.msg ?? 'An error occurred while saving or submitting the request, please try again',
            status: body?.status ?? err?.status ?? 'Request Save/Submission Error',
          });
          dispatch({ type: 'UPDATE_VIOLATION_COMPLAINT_ERRORS', payload: body?.data });
        }
      });
    },
  doSubmitAnonymous:
    (data) =>
    ({ store }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      const signed = Boolean(data.request.isSubmit);
      signed && setIsLoading(true, LoadingMessages.SubmitRequest);

      const hasSupportingDocuments = data?.request?.violationComplaints?.[0]?.hasSupportingDocuments;

      if (hasSupportingDocuments === 'true' && store.selectSelectedFiles()?.length > 0) {
        // Format to FormData
        const formdata = new FormData();
        formdata.append('overwrite', false);
        formdata.append('isAnonymous', true);
        store.selectSelectedFiles().forEach((item) => {
          const fileMetaData = {
            ProjectID: store.selectProjectID(),
            RequestID: store.selectRequestID(),
            Version: 0,
            Type: RequestStatus.Submitted,
            Tags: item?.tags,
          };
          formdata.append('files', item?.file);
          formdata.append('fileMetaData', JSON.stringify(fileMetaData));
        });

        Promise.all([store.doUploadFilesAnonymous(formdata)])
          .then((results) => {
            // if all files upload successfully
            if (results?.[0]?.status === ResponseStatuses.Success) {
              store.doUpdateAnonymousViolationComplaint(data);
            } else {
              // if any of the files failed to upload
              signed && setIsLoading(false);
              store.doResetUsersFileData();
              openSecondaryModal(statusModal, {
                status: 'ERROR! Failed Uploads',
                msg: (
                  <>
                    <p>{results?.[0]?.msg}</p>
                    <p>Retry saving/submitting your Violation Complaint in a few minutes.</p>
                  </>
                ),
                saveText: 'Return to request',
              });
            }
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        // If no files were selected, just update VC
        store.doResetFileSection('Supporting Documents', true);
        store.doUpdateAnonymousViolationComplaint(data);
      }
    },
  doUpdateAnonymousViolationComplaint:
    (data) =>
    ({ apiPut, dispatch }) => {
      const { setIsLoading } = useApp.getState();
      const { openSecondaryModal } = useModal.getState();

      const signed = Boolean(data.request.isSubmit);
      signed && setIsLoading(true, LoadingMessages.SubmitRequest);

      const uri = '/api/ViolationComplaint/updateAnonymousViolationComplaint';
      const skipAuth = true;

      return apiPut(
        uri,
        data,
        (err, body) => {
          const signed = Boolean(data.request.isSubmit);
          if (
            !err &&
            [ResponseStatuses.Success, ResponseStatuses.SuccessWithWarning, ResponseStatuses.Warning].includes(
              body?.status
            )
          ) {
            signed && setIsLoading(false);
            signed && toast.success('Anonymous Violation Complaint has been successfully submitted!');
            signed && router.navigate({ to: '/' });
          } else {
            signed && setIsLoading(false);
            openSecondaryModal(statusModal, {
              msg:
                body?.msg ?? err?.msg ?? 'An error occurred while saving or submitting the request, please try again',
              status: body?.status ?? err?.status ?? 'Request Save/Submission Error',
            });
            dispatch({ type: 'UPDATE_VIOLATION_COMPLAINT_ERRORS', payload: body?.data });
          }
        },
        skipAuth
      );
    },
  doUpdateViolationComplaintRequest:
    (data) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_VIOLATION_COMPLAINT', payload: data });
      dispatch({
        type: 'UPDATE_REQUEST_OBJECT',
        payload: { violationComplaints: [store.selectViolationComplaintData()] },
      });
    },
  doUpdateViolationComplaintRequestContacts:
    (data) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_VIOLATION_COMPLAINT_CONTACTS', payload: data });
      dispatch({
        type: 'UPDATE_REQUEST_OBJECT',
        payload: { violationComplaints: [store.selectViolationComplaintData()] },
      });
    },
  doUpdateIsAnonymous:
    (data) =>
    ({ dispatch }) => {
      dispatch({ type: 'UPDATE_IS_ANONYMOUS', payload: data });
    },
  doResetViolationComplaintRequest:
    () =>
    ({ dispatch, store }) => {
      dispatch({ type: 'RESET_VIOLATION_COMPLAINT' });
      store.doResetUsersFileData();
    },
  doUpdateIsAllegedViolatorKnown:
    (data) =>
    ({ dispatch }) => {
      dispatch({ type: 'UPDATE_VIOLATION_COMPLAINT_IS_ALLEGED_VIOLATOR', payload: data });
    },
};

export default violationsFormDataBundle;
